import { getActiveSubscriptions, clearSubscriptionCache as clearSharedCache } from './subscriptionCache';

/**
 * Forum Access Control Utility
 * Handles checking discussion forum subscription and storage space access
 */

let forumSubscriptionCache = null;
let forumCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n === 0) return '0 Bytes';
  if (n < 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${parseFloat((n / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const isForumModule = (sub) => {
  const moduleName = (sub.SubscriptionPlan?.Module?.name || '').toLowerCase();
  const moduleDisplayName = (sub.SubscriptionPlan?.Module?.display_name || '').toLowerCase();
  return (
    moduleName === 'discussion_forum' ||
    moduleName.includes('forum') ||
    moduleDisplayName.includes('forum') ||
    moduleDisplayName.includes('discussion')
  );
};

const fetchForumSubscription = async () => {
  try {
    const response = await getActiveSubscriptions();

    if (response && response.subscriptions && response.subscriptions.length > 0) {
      const forumSub = response.subscriptions.find(isForumModule);

      if (forumSub) {
        const used = Number(forumSub.forum_storage_used || forumSub.forum_storage?.used || 0);
        const limit = Number(
          forumSub.SubscriptionPlan?.forum_storage_limit ||
            forumSub.forum_storage?.limit ||
            0
        );
        const isUnlimited =
          forumSub.SubscriptionPlan?.is_unlimited_forum_storage ||
          forumSub.forum_storage?.is_unlimited ||
          false;

        return {
          id: forumSub.id,
          corporate_id: forumSub.corporate_id,
          subscription_plan_id: forumSub.subscription_plan_id,
          status: forumSub.status,
          forum_storage_used: used,
          SubscriptionPlan: forumSub.SubscriptionPlan,
          forum_storage: forumSub.forum_storage || {
            used,
            used_formatted: formatBytes(used),
            limit,
            limit_formatted: isUnlimited ? 'Unlimited' : formatBytes(limit),
            available: Math.max(0, limit - used),
            available_formatted: isUnlimited ? 'Unlimited' : formatBytes(Math.max(0, limit - used)),
            is_unlimited: isUnlimited,
            usage_percentage: isUnlimited
              ? '0.00'
              : ((used / (limit || 1)) * 100).toFixed(2),
          },
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching forum subscription:', error);
    return null;
  }
};

export const getForumSubscription = async () => {
  const now = Date.now();

  if (forumSubscriptionCache && forumCacheTimestamp && (now - forumCacheTimestamp) < CACHE_DURATION) {
    return forumSubscriptionCache;
  }

  const subscription = await fetchForumSubscription();
  forumSubscriptionCache = subscription;
  forumCacheTimestamp = now;

  return subscription;
};

export const hasForumSubscription = async () => {
  try {
    const subscription = await getForumSubscription();
    return subscription !== null && subscription.status === 'active';
  } catch (error) {
    console.error('Error checking forum subscription:', error);
    return false;
  }
};

export const hasForumStorageSpace = async (requiredBytes = 0) => {
  try {
    const subscription = await getForumSubscription();

    if (!subscription) {
      return {
        hasSpace: false,
        reason: 'no_subscription',
        message: 'No discussion forum subscription found',
      };
    }

    if (subscription.status !== 'active') {
      return {
        hasSpace: false,
        reason: 'inactive_subscription',
        message: 'Discussion forum subscription is not active',
      };
    }

    if (
      subscription.forum_storage?.is_unlimited ||
      subscription.SubscriptionPlan?.is_unlimited_forum_storage
    ) {
      return {
        hasSpace: true,
        isUnlimited: true,
        available: Infinity,
        available_formatted: 'Unlimited',
      };
    }

    const limit =
      Number(subscription.SubscriptionPlan?.forum_storage_limit) ||
      Number(subscription.forum_storage?.limit) ||
      0;
    const used =
      Number(subscription.forum_storage_used) ||
      Number(subscription.forum_storage?.used) ||
      0;
    const available = limit - used;

    if (available < requiredBytes) {
      return {
        hasSpace: false,
        reason: 'insufficient_space',
        message: `Insufficient forum storage. Required: ${formatBytes(requiredBytes)}, Available: ${formatBytes(available)}`,
        available,
        available_formatted: formatBytes(available),
        required: requiredBytes,
        required_formatted: formatBytes(requiredBytes),
      };
    }

    return {
      hasSpace: true,
      available,
      available_formatted: formatBytes(available),
      used,
      used_formatted: formatBytes(used),
      limit,
      limit_formatted: formatBytes(limit),
      usage_percentage: subscription.forum_storage?.usage_percentage || '0.00',
    };
  } catch (error) {
    console.error('Error checking forum storage space:', error);
    return {
      hasSpace: false,
      reason: 'error',
      message: 'Error checking forum storage space',
    };
  }
};

export const canUploadForumFiles = async (requiredBytes = 0) => {
  try {
    const hasSubscription = await hasForumSubscription();

    if (!hasSubscription) {
      return {
        canUpload: false,
        reason: 'no_subscription',
        message: 'Discussion forum subscription is required to upload files',
      };
    }

    const spaceCheck = await hasForumStorageSpace(requiredBytes);

    if (!spaceCheck.hasSpace) {
      return {
        canUpload: false,
        reason: spaceCheck.reason,
        message: spaceCheck.message,
        ...spaceCheck,
      };
    }

    return {
      canUpload: true,
      ...spaceCheck,
    };
  } catch (error) {
    console.error('Error checking forum upload permission:', error);
    return {
      canUpload: false,
      reason: 'error',
      message: 'Error checking forum upload permission',
    };
  }
};

export const clearForumSubscriptionCache = () => {
  forumSubscriptionCache = null;
  forumCacheTimestamp = null;
  clearSharedCache();
};
